import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import {
  AccountCircle,
  Lock,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from "@mui/icons-material";
import { login } from "../api/BaseCalls";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../context/snackbarContext";
import { useAtomValue, useSetAtom } from "jotai";
import {
  expiryAtom,
  isLoggedInAtom,
  sessionAtom,
  userAtom,
} from "../atoms/auth";

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { showSnackbar } = useSnackbar();
  const setSession = useSetAtom(sessionAtom);
  const setExpiry = useSetAtom(expiryAtom);
  const setUser = useSetAtom(userAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/logs/email-flow");
    }
  }, [isLoggedIn, navigate]);

  // --- THE FIX: CSS Hack to prevent browser autofill background ---
  const autofillStyles = {
    "& .MuiInputBase-input": {
      "&:-webkit-autofill": {
        // Transition the background color change so it takes 5000s (effectively never happens)
        transition: "background-color 5000s ease-in-out 0s",
        // Force the text color to match your theme
        WebkitTextFillColor: theme.palette.text.primary,
      },
    },
  };

  const handleLogin = async (user_id, password) => {
    try {
      setLoading(true);
      const expiryTime =
        new Date().getTime() + 3 * 60 * 60 * 1000 - 5 * 60 * 1000;

      const data = await login(user_id, password);

      setSession(data["Session-ID"]);
      setExpiry(expiryTime);
      setUser(user_id);

      showSnackbar(`Welcome back, ${user_id}!`, "success");

      setTimeout(() => {
        navigate("/logs/email-flow");
      }, 1500);
    } catch (error) {
      showSnackbar(error.message || "Login failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const user_id = formData.get("user_id");
    const password = formData.get("password");
    await handleLogin(user_id, password);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  return (
    <Box
      sx={{
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundSize: "400% 400%",
        animation: "gradient 15s ease infinite",

        "@keyframes gradient": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
        padding: 2,
      }}
    >
      <Paper
        elevation={theme.palette.mode === "light" ? 3 : 10}
        sx={{
          padding: 5,
          borderRadius: 4,
          width: "100%",
          maxWidth: 450,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backdropFilter: "blur(20px)",
          backgroundColor:
            theme.palette.mode === "light"
              ? alpha("#ffffff", 0.8)
              : alpha("#1e293b", 0.8),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: "50%",
            mb: 2,
          }}
        >
          <LoginIcon color="primary" sx={{ fontSize: 40 }} />
        </Box>

        <Typography
          component="h1"
          variant="h4"
          fontWeight="700"
          gutterBottom
          color="text.primary"
        >
          MailFlow Logs
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Sign in to access log monitor
        </Typography>

        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <TextField
            name="user_id"
            label="User ID"
            variant="outlined"
            fullWidth
            required
            autoComplete="username"
            // Combine margin bottom with autofill styles
            sx={{ mb: 3, ...autofillStyles }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            required
            autoComplete="current-password"
            // Combine margin bottom with autofill styles
            sx={{ mb: 4, ...autofillStyles }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: "0 4px 14px 0 rgba(0,118,255,0.39)",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "scale(1.02)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 4 }}>
          © {new Date().getFullYear()} Admin Panel. All rights reserved.
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginPage;
